/**
 * Google Play Integrity: proves to the SalunBD API that sign-up, sign-in and booking come from this app installed from
 * Google Play on a genuine Android device (the server's half is salon-backend-service/src/common/integrity).
 *
 * Before one of those requests the app asks the API for a one-time value, has Google sign it into a token, and sends both
 * with the request (X-Integrity-Nonce, X-Integrity-Token). Anything that goes wrong here (no Google Play services, no network
 * for Google, the feature switched off on the server) sends the request without them: the server decides what a missing token
 * means (it only refuses once it is switched to enforce), so this never blocks someone by itself.
 *
 * Android only, and only with EXPO_PUBLIC_PLAY_INTEGRITY_PROJECT_NUMBER set (the Google Cloud project number linked in the
 * Play Console). The website and iOS never load the native module.
 */
import { Platform } from 'react-native';

export type IntegrityAction = 'register' | 'login' | 'google' | 'book';

/** The requests that carry proof. Must match the routes decorated with @RequireIntegrity on the server. */
const ROUTES: Record<string, IntegrityAction> = {
  'POST /auth/register': 'register',
  'POST /auth/login': 'login',
  'POST /auth/google': 'google',
  'POST /appointments/book': 'book',
  'POST /appointments/book-many': 'book',
};

const PROJECT_NUMBER = process.env.EXPO_PUBLIC_PLAY_INTEGRITY_PROJECT_NUMBER?.trim() ?? '';
const STEP_TIMEOUT_MS = 10_000;
const OFF_FOR_MS = 5 * 60_000;

type AppIntegrity = typeof import('@expo/app-integrity');
type FetchFn = typeof fetch;

let provider: Promise<AppIntegrity> | null = null;
let offUntil = 0;

/** Which action (if any) a request to the API is one of the few that carry proof, on any platform. */
export function routeActionFor(method: string, apiBase: string, url: string): IntegrityAction | null {
  const path = url.slice(apiBase.replace(/\/+$/, '').length).split('?')[0].replace(/\/+$/, '');
  return ROUTES[`${method.toUpperCase()} ${path}`] ?? null;
}

/** Which action (if any) a request to the API needs Play Integrity proof for: only on Android, and only once a project number is set. */
export function integrityActionFor(method: string, apiBase: string, url: string): IntegrityAction | null {
  if (Platform.OS !== 'android' || !PROJECT_NUMBER) return null;
  return routeActionFor(method, apiBase, url);
}

function withTimeout<T>(work: Promise<T>, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${what} took too long`)), STEP_TIMEOUT_MS);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** Google's side is set up once per app start; a failed attempt is retried on the next request. */
function loadProvider(): Promise<AppIntegrity> {
  provider ??= (async () => {
    const mod = await import('@expo/app-integrity');
    await mod.prepareIntegrityTokenProviderAsync(PROJECT_NUMBER);
    return mod;
  })().catch((err) => {
    provider = null;
    throw err;
  });
  return provider;
}

/** The two headers for one request, or none when proof is off, not possible here, or failed. Never throws. */
export async function integrityHeaders(action: IntegrityAction, nativeFetch: FetchFn, apiBase: string, clientHeaders: Record<string, string>): Promise<Record<string, string>> {
  if (Platform.OS !== 'android' || !PROJECT_NUMBER || Date.now() < offUntil) return {};
  try {
    const res = await withTimeout(nativeFetch(`${apiBase.replace(/\/+$/, '')}/integrity/nonce?action=${action}`, { headers: clientHeaders }), 'The integrity nonce');
    const answer = res.ok ? ((await res.json().catch(() => null)) as { enabled?: boolean; nonce?: string } | null) : null;
    if (!answer?.enabled || !answer.nonce) {
      offUntil = Date.now() + OFF_FOR_MS; // the server is not asking; do not ask it again for a few minutes
      return {};
    }
    const mod = await withTimeout(loadProvider(), 'Preparing Play Integrity');
    const token = await withTimeout(mod.requestIntegrityCheckAsync(answer.nonce), 'The integrity token');
    return { 'x-integrity-token': token, 'x-integrity-nonce': answer.nonce };
  } catch (err) {
    if (__DEV__) console.warn(`[integrity] sending without proof: ${(err as Error).message}`);
    return {};
  }
}
